/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DirtyType } from '@gh/common-shared-data';
import { arrayOf, date, DateFormat, entity, enumeration, subentity } from '@gh/core-data';
import { FormulaMapper, UomQuantityMapper } from '../../common';
import { CostCenterSupplierFinderListItemDto } from '../../cost-centers';
import { GtoUom } from '../../take-off-params/top.dto';
import {
  JobOrderDelivery,
  JobOrderDetailsMapper,
  JobOrderMethod,
  JobOrdersForScheduleMapper,
} from '../orders';
import {
  JeAuthorizeResponseDto,
  JeCcCompleteDto,
  JeCcOrderDetailsDto,
  JeCcSaveDto,
  JeComponentDto,
  JeComponentSaveDto,
  JeComponentWithItemsDto,
  JeDetailsDto,
  JeDirtyDataDto,
  JeGenerateItemIdsResponseDto,
  JeGetComponentsResponseDto,
  JeItemDto,
  JeItemSaveDto,
  JeOrderGeneratedDto,
  JeOrderSaveDto,
  JeTopRefItemDto,
  JeTopSaveDto,
  JeVariationStatus,
  JeVariationType,
} from './job-estimate.dto';

export const JeItemMapper = entity<JeItemDto>({
  variationType: enumeration(JeVariationType),
  variationStatus: enumeration(JeVariationStatus),
  formula: FormulaMapper,
});

export const JeComponentMapper = entity<JeComponentDto>({
  variationType: enumeration(JeVariationType),
  variationStatus: enumeration(JeVariationStatus),
  formula: FormulaMapper,
});

export const JeComponentWithItemsMapper = entity<JeComponentWithItemsDto>({
  formula: FormulaMapper,
});

export const JeItemSaveMapper = entity<JeItemSaveDto>({
  variationType: enumeration(JeVariationType),
  variationStatus: enumeration(JeVariationStatus),
  formula: FormulaMapper,
  dirtyType: enumeration(DirtyType),
});

export const JeComponentSaveMapper = entity<JeComponentSaveDto>({
  variationType: enumeration(JeVariationType),
  variationStatus: enumeration(JeVariationStatus),
  formula: FormulaMapper,
  dirtyType: enumeration(DirtyType),
});

export const JeOrderSaveMapper = entity<JeOrderSaveDto>({
  deliveryDate: date(DateFormat.Date),
  deliveryScheduleDate: date(DateFormat.Date),
  producedDate: date(DateFormat.DateTime),
  supplierOrderBy: enumeration(JobOrderMethod),
  method: enumeration(JobOrderMethod),
  producedMethod: enumeration(JobOrderMethod),
  delivery: enumeration(JobOrderDelivery),
  dirtyType: enumeration(DirtyType),
});

export const JeOrderGeneratedMapper = entity<JeOrderGeneratedDto>({
  method: enumeration(JobOrderMethod),
});
export const JeDetailsMapper = entity<JeDetailsDto>(
  {
    jobComponents: arrayOf(JeComponentMapper),
    jobItems: arrayOf(JeItemMapper),
    jobOrders: arrayOf(JobOrderDetailsMapper),
    jobOrdersForSchedule: arrayOf(JobOrdersForScheduleMapper),
    tops: arrayOf(subentity<JeTopRefItemDto>({
    gtoUom: enumeration(GtoUom),
  })),
});

export const JeDetailsDirtyDataMapper = entity<JeDirtyDataDto>({
  jobCostCenters: arrayOf(subentity<JeCcSaveDto>({
    dirtyType: enumeration(DirtyType),
  })),
  jobComponents: arrayOf(JeComponentSaveMapper),
  jobItems: arrayOf(JeItemSaveMapper),
  jobOrders: arrayOf(JeOrderSaveMapper),
  jobTops: arrayOf(subentity<JeTopSaveDto>({
    value: UomQuantityMapper,
  })),
});

export const JeGetCcCompleteMapper = entity<JeCcCompleteDto>({
  jobItems: arrayOf(JeItemMapper),
  jobOrders: arrayOf(JeOrderSaveMapper),
  suppliers: arrayOf(subentity<CostCenterSupplierFinderListItemDto>({
    orderBy: enumeration(JobOrderMethod),
  })),
});

export const JeGetComponentsResponseMapper = entity<JeGetComponentsResponseDto>({
  components: arrayOf(JeComponentWithItemsMapper),
});

export const JeGenerateItemIdsResponseMapper = entity<JeGenerateItemIdsResponseDto>({});

export const JeAuthorizeResponseMapper = entity<JeAuthorizeResponseDto>({});

export const JeCcOrderDetailsMapper = entity<JeCcOrderDetailsDto>({});

export const EstimateOrderMapper = entity<any>({
  startDate: date(DateFormat.DateTime),
  deliveryDate: date(DateFormat.DateTime),
});

export const EstimateOrdersMapper = entity<any>({
  jobOrders: arrayOf(EstimateOrderMapper),
});
